import * as XLSX from "xlsx";
import { assetCategoryFromCic, cicBucket, CicBucket } from "./fundParseCicClassification";
import { TptRow } from "./fundParseTptTypes";
import { textToBase64 } from "./fundParseOutputManifestBuilder";

const COL_CIC = "12_CIC_code_of_the_instrument";
const CSV_LINE_TERMINATOR = "\r\n";

const ID_TYPE_LABELS: Record<string, string> = {
    "1": "ISIN",
    "2": "CUSIP",
    "3": "SEDOL",
    "4": "WKN",
    "52019": "Bloomberg",
    "5": "Bloomberg",
    "9": "Other",
    "99": "Not_available"
};

const BOND_XLSX_COLUMNS = [
    "Fund LT Date",
    "Fund ID Code",
    "Fund ID Code Type",
    "Bloomberg Security Ticker",
    "Weight Nominal",
    "Weight MV",
    "Is Covered Bond",
    "Item Title",
    "Issuer Name",
    "Issuer Code",
    "Issuer Type of Code",
    "Issuer Group Name",
    "Issuer Group Code",
    "Issuer Group Type of Code",
    "Issuer Country",
    "Currency (ISO code)",
    "External rating",
    "Rating agency",
    "First Call Date",
    "Maturity date",
    "Issue date",
    "Coupon",
    "Coupon freq.",
    "First Coupon date",
    "FRN Ref Rate",
    "FRN Ref Rate Add-On",
    "Next Coupon Date",
    "Non-EEA Sovereign bond issued in domestic currency",
    "EEA Guaranteed",
    "Type of exposure",
    "Solvency ratio (Type 2 only)",
    "Issuer Sector NACE",
    "Issuer Sector GICS",
    "Modified Duration",
    "Par Amount",
    "Percentage of Par Amount",
    "Type of Infrastructure Investment",
    "Asset Category",
    "CIC Code",
    "TotalSIIAmount",
    "NominalAmount"
];

const EQUITY_XLSX_COLUMNS = [
    "Fund LT Date",
    "Fund ID Code",
    "Fund ID Code Type",
    "Bloomberg Security Ticker",
    "Weight MV",
    "Security Title",
    "Issuer",
    "Issuer Code",
    "Issuer Type of Code",
    "Issuer Group",
    "Issuer Group Code",
    "Issuer Group Type of Code",
    "Issuer Country",
    "Currency",
    "External rating",
    "Rating agency",
    "Is Strategic Holding",
    "Type of Equity",
    "Issuer Sector NACE",
    "Issuer Sector GICS",
    "Type of exposure",
    "Solvency ratio (Type 2 only)",
    "Asset Category",
    "CIC Code",
    "Type of Infrastructure Investment"
];

const CASH_XLSX_COLUMNS = [
    "Fund LT Date",
    "Fund ID Code",
    "Fund ID Code Type",
    "Weight MV",
    "Security Title",
    "",
    "",
    "",
    "Issuer",
    "Issuer Code",
    "Issuer Type of Code",
    "Issuer Group",
    "Issuer Group Code",
    "Issuer Group Type of Code",
    "Issuer Country",
    "",
    "Currency",
    "External rating",
    "Rating agency",
    "",
    "Issuer Sector NACE",
    "Issuer Sector GICS",
    "Type of exposure",
    "Asset Category",
    "CIC Code",
    "EEA Guaranteed",
    "Weight Nominal"
];

const MAX_ISSUER_SECTOR_GICS_LEN = 100;
const MAX_CASH_FIELD_LEN = 50;
const ISSUER_SECTOR_GICS_ALLOWED_RE = /[^A-Za-z0-9 ]/g;

function strCell(row: TptRow, key: string, defaultValue = ""): string {
    const value = row[key];
    if (value === null || typeof value === "undefined") {
        return defaultValue;
    }
    if (typeof value === "number" && Number.isFinite(value) && value === Math.trunc(value)) {
        return String(Math.trunc(value));
    }
    const text = String(value).trim();
    if (!text || ["nan", "nat", "none"].includes(text.toLowerCase())) {
        return defaultValue;
    }
    return text;
}

function fmtDate(value: unknown): string {
    if (value === null || value === "" || typeof value === "undefined") {
        return "";
    }
    const text = String(value).trim();
    if (!text || ["nan", "nat", "none"].includes(text.toLowerCase())) {
        return "";
    }
    return text.split(/\s+/)[0];
}

function fmtNum(value: unknown, decimals: number | null = 6): string {
    if (value === null || value === "" || typeof value === "undefined") {
        return "";
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return String(value).trim();
    }
    if (decimals === null) {
        if (parsed === Math.trunc(parsed)) {
            return String(Math.trunc(parsed));
        }
        return String(parsed).replace(/\.?0+$/, "").replace(".", ",");
    }
    return parsed.toFixed(decimals).replace(/\.?0+$/, "").replace(".", ",");
}

function isZeroWeight(value: string): boolean {
    if (!value.trim()) {
        return true;
    }
    const normalized = value.trim().replace(",", ".");
    if (normalized === "0") {
        return true;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed === 0;
}

function fmtWeight(value: unknown, decimals: number | null = 6): string {
    const formatted = fmtNum(value, decimals);
    return isZeroWeight(formatted) ? "" : formatted;
}

function normIdType(value: unknown): string {
    if (value === null || value === "" || typeof value === "undefined") {
        return "";
    }
    const key = typeof value === "number" && value === Math.trunc(value) ? String(Math.trunc(value)) : String(value).trim();
    return ID_TYPE_LABELS[key] || key;
}

function leiChecksumOk(lei: string): boolean {
    if (!/^[0-9A-Z]{20}$/.test(lei)) {
        return false;
    }
    const expanded = lei
        .split("")
        .map((char) => parseInt(char, 36).toString())
        .join("");
    return Number(BigInt(expanded) % 97n) === 1;
}

function normalizedLeiFields(row: TptRow, codeKey: string): [string, string] {
    const rawCode = strCell(row, codeKey).toUpperCase();
    if (!rawCode || rawCode === "00000000000000000000") {
        return ["", ""];
    }
    if (leiChecksumOk(rawCode)) {
        return [rawCode, "LEI"];
    }
    return ["", ""];
}

function issuerSectorGics(sectorText: string): string {
    if (!sectorText) {
        return "";
    }
    const cleaned = sectorText.replace(/;/g, " ").replace(ISSUER_SECTOR_GICS_ALLOWED_RE, "").replace(/\s+/g, " ").trim();
    return cleaned.length > MAX_ISSUER_SECTOR_GICS_LEN ? cleaned.slice(0, MAX_ISSUER_SECTOR_GICS_LEN) : cleaned;
}

function sectorDescriptionFromNaceCode(row: TptRow, naceDescriptions: Record<string, string>): string {
    const naceCode = strCell(row, "54_Economic_sector");
    if (!naceCode) {
        return "";
    }
    return naceDescriptions[naceCode] || "";
}

function coveredYn(row: TptRow): string {
    const value = strCell(row, "55_Covered_not_covered").toUpperCase();
    if (value === "C") {
        return "Y";
    }
    if (value === "NC") {
        return "N";
    }
    return value ? "N" : "";
}

function ratingLabel(row: TptRow): string {
    return strCell(row, "59_Credit_quality_step");
}

function hasCreditRatingFromStep(row: TptRow): boolean {
    return Boolean(ratingLabel(row));
}

function ratingAgencyLabel(row: TptRow): string {
    if (hasCreditRatingFromStep(row)) {
        return "Received from fund manager";
    }
    const agency = strCell(row, "113_Rating_agency");
    if (agency) {
        return agency;
    }
    const creditRating = strCell(row, "112_Credit_rating");
    if (creditRating) {
        return "One of S&P, Moody's, Fitch or NCR";
    }
    return "";
}

function creditRatingFromStep(row: TptRow): string {
    return ratingLabel(row) || "NR";
}

function isExplicitGuaranteeYes(row: TptRow, field: string): boolean {
    return ["Y", "1", "YES"].includes(strCell(row, field).toUpperCase());
}

function isEeaIssuer(row: TptRow): boolean {
    return strCell(row, "53_Issuer_economic_area") === "1";
}

function equityTypeFromCic(cic: string): string {
    const suffix = cic.trim().slice(-2);
    return suffix === "34" ? "Type 2" : "Type 1";
}

function bondRow(row: TptRow, naceDescriptions: Record<string, string>): string[] {
    const weight =
        fmtWeight(row["26_Valuation_weight"]) ||
        fmtWeight(row["30_Market_exposure_in_weight"]);
    const cic = strCell(row, COL_CIC);
    const [issuerCode, issuerCodeType] = normalizedLeiFields(row, "47_Issuer_identification_code");
    const [issuerGroupCode, issuerGroupCodeType] = normalizedLeiFields(row, "50_Identification_of_the_group");
    const sectorText = sectorDescriptionFromNaceCode(row, naceDescriptions);
    return [
        fmtDate(row["6_Valuation_date"]) || fmtDate(row["7_Reporting_date"]),
        strCell(row, "1_Portfolio_identifying_data"),
        normIdType(row["2_Type_of_identification_code_for_the_fund_share_or_portfolio"]),
        "",
        weight,
        weight,
        coveredYn(row),
        strCell(row, "17_Instrument_name"),
        strCell(row, "46_Issuer_name"),
        issuerCode,
        issuerCodeType,
        strCell(row, "49_Name_of_the_group_of_the_issuer"),
        issuerGroupCode,
        issuerGroupCodeType,
        strCell(row, "52_Issuer_country"),
        strCell(row, "21_Quotation_currency_(A)"),
        ratingLabel(row),
        ratingAgencyLabel(row),
        fmtDate(row["43_Call_put_date"]),
        fmtDate(row["39_Maturity_date"]),
        fmtDate(row["63_Effective_date_of_instrument"]),
        fmtNum(row["33_Coupon_rate"], 8),
        fmtNum(row["38_Coupon_payment_frequency"], null),
        "",
        strCell(row, "36_Interest_rate_index_name") || strCell(row, "34_Interest_rate_reference_identification"),
        fmtNum(row["37_Interest_rate_margin"], 6),
        "",
        isExplicitGuaranteeYes(row, "57_Explicit_guarantee_by_the_country_of_issue") && isEeaIssuer(row) ? "Y" : "N",
        isExplicitGuaranteeYes(row, "58_Explicit_guarantee_by_the_country_of_issue_of_the_underlying_asset") ? "Y" : "N",
        "1",
        "",
        strCell(row, "54_Economic_sector"),
        sectorText,
        fmtNum(row["90_Modified_duration_to_maturity_date"], 6),
        fmtNum(row["19_Nominal_amount"], null),
        fmtNum(row["26_Valuation_weight"], 8),
        "0",
        assetCategoryFromCic(cic),
        cic,
        fmtNum(row["24_Market_valuation_in_portfolio_currency_(B)"], 4),
        fmtNum(row["19_Nominal_amount"], 4)
    ];
}

function equityRow(row: TptRow, naceDescriptions: Record<string, string>): string[] {
    const weight =
        fmtWeight(row["26_Valuation_weight"]) ||
        fmtWeight(row["30_Market_exposure_in_weight"]);
    const cic = strCell(row, COL_CIC);
    const [issuerCode, issuerCodeType] = normalizedLeiFields(row, "47_Issuer_identification_code");
    const [issuerGroupCode, issuerGroupCodeType] = normalizedLeiFields(row, "50_Identification_of_the_group");
    return [
        fmtDate(row["6_Valuation_date"]) || fmtDate(row["7_Reporting_date"]),
        strCell(row, "1_Portfolio_identifying_data"),
        normIdType(row["2_Type_of_identification_code_for_the_fund_share_or_portfolio"]),
        "",
        weight,
        strCell(row, "17_Instrument_name"),
        strCell(row, "46_Issuer_name"),
        issuerCode,
        issuerCodeType,
        strCell(row, "49_Name_of_the_group_of_the_issuer"),
        issuerGroupCode,
        issuerGroupCodeType,
        strCell(row, "52_Issuer_country"),
        strCell(row, "21_Quotation_currency_(A)"),
        creditRatingFromStep(row),
        ratingAgencyLabel(row),
        "N",
        equityTypeFromCic(cic),
        strCell(row, "54_Economic_sector"),
        sectorDescriptionFromNaceCode(row, naceDescriptions),
        "1",
        "0",
        assetCategoryFromCic(cic),
        cic,
        "1"
    ];
}

function cashRow(row: TptRow, naceDescriptions: Record<string, string>): string[] {
    const weightMv =
        fmtWeight(row["26_Valuation_weight"]) ||
        fmtWeight(row["30_Market_exposure_in_weight"]);
    const cic = strCell(row, COL_CIC);
    const [issuerCode, issuerCodeType] = normalizedLeiFields(row, "47_Issuer_identification_code");
    const [issuerGroupCode, issuerGroupCodeType] = normalizedLeiFields(row, "50_Identification_of_the_group");
    return [
        fmtDate(row["6_Valuation_date"]) || fmtDate(row["7_Reporting_date"]),
        strCell(row, "1_Portfolio_identifying_data"),
        normIdType(row["2_Type_of_identification_code_for_the_fund_share_or_portfolio"]),
        weightMv,
        strCell(row, "17_Instrument_name"),
        strCell(row, "14_Identification_code_of_the_instrument"),
        normIdType(row["15_Type_of_identification_code_for_the_instrument"]),
        strCell(row, "106_Asset_pledged_as_collateral"),
        strCell(row, "46_Issuer_name"),
        issuerCode,
        issuerCodeType,
        strCell(row, "49_Name_of_the_group_of_the_issuer"),
        issuerGroupCode,
        issuerGroupCodeType,
        strCell(row, "52_Issuer_country"),
        strCell(row, "123a_Fund_custodian_country"),
        strCell(row, "21_Quotation_currency_(A)"),
        ratingLabel(row),
        ratingAgencyLabel(row),
        strCell(row, "110_Valorisation_method"),
        strCell(row, "54_Economic_sector"),
        sectorDescriptionFromNaceCode(row, naceDescriptions),
        "0",
        assetCategoryFromCic(cic),
        cic,
        "N",
        weightMv
    ];
}

function cleanBondRow(parts: string[]): string[] {
    const cleaned = [...parts];
    if (cleaned[23] === "0") {
        cleaned[23] = "";
    }
    cleaned[31] = issuerSectorGics(cleaned[31]);
    return cleaned;
}

function cleanEquityRow(parts: string[]): string[] {
    const cleaned = [...parts];
    cleaned[19] = issuerSectorGics(cleaned[19]);
    return cleaned;
}

function cleanCashRow(parts: string[]): string[] {
    const cleaned = parts.map((value) => (value.length > MAX_CASH_FIELD_LEN ? value.slice(0, MAX_CASH_FIELD_LEN) : value));
    const withoutZeros = cleaned.map((value) => (isZeroWeight(value) ? "" : value));
    if (!withoutZeros[26] || isZeroWeight(withoutZeros[26])) {
        withoutZeros[26] = withoutZeros[3] && !isZeroWeight(withoutZeros[3]) ? withoutZeros[3] : "";
    }
    return withoutZeros;
}

function paddedHeaderFields(prefix: string, rowCount: number, period: string, columnCount: number): string[] {
    const timestamp = new Date().toISOString().replace("T", " ").slice(0, 19);
    return [prefix, String(rowCount), timestamp, period, ...Array(Math.max(columnCount - 4, 0)).fill("")];
}

function csvContent(headerRow: string[], rows: string[][]): string {
    const escapeCell = (value: string) => {
        if (value.includes('"') || value.includes(";") || value.includes("\n")) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    };
    const lines = [headerRow, ...rows].map((row) => row.map((cell) => escapeCell(cell)).join(";"));
    return lines.join(CSV_LINE_TERMINATOR) + CSV_LINE_TERMINATOR;
}

function periodFromRows(rows: TptRow[], explicitPeriod?: string): string {
    if (explicitPeriod) {
        return explicitPeriod.replace(/\D/g, "").slice(0, 8);
    }
    for (const column of ["7_Reporting_date", "6_Valuation_date"]) {
        for (const row of rows) {
            const formatted = fmtDate(row[column]);
            if (formatted) {
                return formatted.replace(/-/g, "").slice(0, 8);
            }
        }
    }
    return new Date().toISOString().slice(0, 7).replace("-", "");
}

export interface LookthroughExportFile {
    fileName: string;
    base64: string;
    mimeType: string;
}

export interface LookthroughExportStats {
    period: string;
    bond: number;
    equity: number;
    cash: number;
    derivative: number;
    unknown_cic: number;
}

export function exportLookthroughFiles(
    rows: TptRow[],
    naceDescriptions: Record<string, string>,
    options: {
        period?: string;
        createInspectionXlsx?: boolean;
    } = {}
): { files: LookthroughExportFile[]; stats: LookthroughExportStats } {
    const buckets: Record<CicBucket, TptRow[]> = {
        bond: [],
        equity: [],
        cash: [],
        derivative: [],
        unknown: []
    };

    for (const row of rows) {
        buckets[cicBucket(row[COL_CIC])].push(row);
    }

    const bondRows = buckets.bond.map((row) => cleanBondRow(bondRow(row, naceDescriptions)));
    const equityRows = buckets.equity.map((row) => cleanEquityRow(equityRow(row, naceDescriptions)));
    const cashRows = buckets.cash.map((row) => cleanCashRow(cashRow(row, naceDescriptions)));
    const period = periodFromRows(rows, options.period);

    const files: LookthroughExportFile[] = [];
    const exports: Array<[string, string[][], string[], string[]]> = [
        [`${period}_BondLookThrough.csv`, bondRows, ["BondLookThrough"], BOND_XLSX_COLUMNS],
        [`${period}_EquityLookThrough.csv`, equityRows, ["EquityLookThrough"], EQUITY_XLSX_COLUMNS],
        [`${period}_CashLookThrough.csv`, cashRows, ["CashLookThrough"], CASH_XLSX_COLUMNS]
    ];

    for (const [fileName, dataRows, headerPrefix, xlsxColumns] of exports) {
        const columnCount = dataRows[0]?.length || xlsxColumns.length;
        const headerRow = paddedHeaderFields(headerPrefix[0], dataRows.length, period, columnCount);
        const csvText = csvContent(headerRow, dataRows);
        files.push({
            fileName,
            base64: textToBase64(csvText),
            mimeType: "text/csv"
        });

        if (options.createInspectionXlsx && dataRows.length) {
            const sheet = XLSX.utils.aoa_to_sheet([xlsxColumns, ...dataRows]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, sheet, "Sheet1");
            const xlsxBytes = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
            const xlsxBinary = Array.from(new Uint8Array(xlsxBytes as ArrayBuffer))
                .map((byte) => String.fromCharCode(byte))
                .join("");
            files.push({
                fileName: fileName.replace(/\.csv$/i, ".xlsx"),
                base64: btoa(xlsxBinary),
                mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
        }
    }

    if (buckets.derivative.length || buckets.unknown.length) {
        const workbook = XLSX.utils.book_new();
        if (buckets.derivative.length) {
            const derivativeSheet = XLSX.utils.json_to_sheet(
                buckets.derivative.map((row) => ({ _export_bucket: "derivative", ...row }))
            );
            XLSX.utils.book_append_sheet(workbook, derivativeSheet, "Derivatives");
        }
        if (buckets.unknown.length) {
            const unknownSheet = XLSX.utils.json_to_sheet(
                buckets.unknown.map((row) => ({ _export_bucket: "unknown_cic", ...row }))
            );
            XLSX.utils.book_append_sheet(workbook, unknownSheet, "Unclassified_CIC");
        }
        const xlsxBytes = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
        const xlsxBinary = Array.from(new Uint8Array(xlsxBytes as ArrayBuffer))
            .map((byte) => String.fromCharCode(byte))
            .join("");
        files.push({
            fileName: `${period}_ExcludedPositions.xlsx`,
            base64: btoa(xlsxBinary),
            mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
    }

    return {
        files,
        stats: {
            period,
            bond: bondRows.length,
            equity: equityRows.length,
            cash: cashRows.length,
            derivative: buckets.derivative.length,
            unknown_cic: buckets.unknown.length
        }
    };
}

export function rowsToConsolidatedTptXlsx(rows: TptRow[], fileName: string): LookthroughExportFile {
    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Sheet1");
    const xlsxBytes = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    const xlsxBinary = Array.from(new Uint8Array(xlsxBytes as ArrayBuffer))
        .map((byte) => String.fromCharCode(byte))
        .join("");
    return {
        fileName,
        base64: btoa(xlsxBinary),
        mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
}

export function isEquityOrBondCic(cicValue: unknown): boolean {
    return cicBucket(cicValue) === "equity" || cicBucket(cicValue) === "bond";
}

export function createEsgInputRows(rows: TptRow[]): TptRow[] {
    const outputColumns = [
        "1_Portfolio_identifying_data",
        "3_Portfolio_name",
        "46_Issuer_name",
        "47_Issuer_identification_code",
        "48_Type_of_identification_code_for_issuer"
    ];
    const filtered = rows.filter((row) => isEquityOrBondCic(row[COL_CIC]));
    const seenValidLeiByPortfolio: Record<string, Set<string>> = {};
    const seenNameWithoutValidLeiByPortfolio: Record<string, Set<string>> = {};
    const deduplicated: TptRow[] = [];

    for (const row of filtered) {
        const portfolioKey = strCell(row, "1_Portfolio_identifying_data").replace(/\s/g, "").toUpperCase();
        const issuerNameKey = strCell(row, "46_Issuer_name").toUpperCase();
        const [issuerLei] = normalizedLeiFields(row, "47_Issuer_identification_code");

        if (issuerLei) {
            const seen = seenValidLeiByPortfolio[portfolioKey] || new Set<string>();
            if (seen.has(issuerLei)) {
                continue;
            }
            seen.add(issuerLei);
            seenValidLeiByPortfolio[portfolioKey] = seen;
        } else {
            const seen = seenNameWithoutValidLeiByPortfolio[portfolioKey] || new Set<string>();
            if (seen.has(issuerNameKey)) {
                continue;
            }
            seen.add(issuerNameKey);
            seenNameWithoutValidLeiByPortfolio[portfolioKey] = seen;
        }

        const nextRow: TptRow = {};
        for (const column of outputColumns) {
            nextRow[column] = row[column] ?? "";
        }
        deduplicated.push(nextRow);
    }

    return deduplicated;
}

export function esgInputToXlsx(rows: TptRow[], fileName: string): LookthroughExportFile {
    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Sheet1");
    const xlsxBytes = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    const xlsxBinary = Array.from(new Uint8Array(xlsxBytes as ArrayBuffer))
        .map((byte) => String.fromCharCode(byte))
        .join("");
    return {
        fileName,
        base64: btoa(xlsxBinary),
        mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
}
